import { useMemo, useRef, useState } from 'react'
import { Box, LinearProgress, Menu, MenuItem } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import PublishIcon from '@mui/icons-material/Publish'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { FONT_MONO, FONT_SANS, PillButton, TOKENS } from './SchedulerDesign'
import { PublishScheduleDialog } from './PublishSchedule.dialog'
import { ScheduleVariantSelect } from './ScheduleVariantSelect.view'
import {
  RunPhase,
  SchedulerProgress,
  WeekResult,
  useConsolidatedWeeks,
  usePermissions,
} from './Scheduler.data'
import {
  useCreateScheduleVersion,
  useSchedulerData,
} from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { weekBaseHourFor } from 'Features/SchedulerView/ClipToWeek'
import { makespanForWeek } from './makespan'
import { CreateVersionDialog } from './CreateVersion.dialog'
import {
  useScheduleVariants,
  ScheduleVariantKey,
} from 'Features/SchedulerView/SchedulerView.data'

// Mirrors ScheduleViewPage.tsx's getElapsedHoursSinceSundayMidnight — the
// backend's create-version API expects hours elapsed since the most recent
// Monday 00:00, derived from the version's picked date/time.
const hoursSinceMondayMidnight = (time: string) => {
  const now = dayjs(time)
  const lastMondayMidnight =
    now.day() === 1
      ? now.startOf('day')
      : now.subtract((now.day() + 6) % 7, 'day').startOf('day')
  return now.diff(lastMondayMidnight, 'hour')
}

const stageColor = (stage?: string) => {
  if (stage === 'failed') return TOKENS.stateFailed
  if (stage === 'completed') return TOKENS.stateComplete
  return TOKENS.stateRunning
}

/** The headline for each phase the API reports. */
const PHASE_LABEL: Record<RunPhase, string> = {
  queued: 'Queued',
  starting: 'Starting a worker',
  loading: 'Loading data',
  preparing: 'Preparing data',
  optimising: 'Optimising',
  saving: 'Saving results',
  completed: 'Finishing',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/**
 * "Run scheduler" as the planner sees it: one run in four steps, although it
 * is two jobs underneath (the survey, then the run it hands over to).
 */
const RUN_STEPS = ['Starting', 'Preparing data', 'Optimising', 'Saving results']

const stepIndex = (isPreparing: boolean, phase?: RunPhase) => {
  if (isPreparing) {
    return !phase || phase === 'queued' || phase === 'starting' ? 0 : 1
  }
  // A page that reattaches during the survey watches it through the RUN
  // hook (it follows the survey into its run), so the phase decides here.
  if (phase === 'preparing') return 1
  if (phase === 'saving') return 3
  if (phase === 'completed') return 4
  return 2
}

/** No sign of life for this long and the banner says so. The API itself
 *  fails a run whose worker has gone (after 45s it checks; 3 min at most). */
const STALL_WARNING_SECS = 60

const sinceLabel = (secs: number) =>
  secs < 5
    ? 'Updated just now'
    : secs < 90
      ? `Updated ${secs}s ago`
      : `Updated ${Math.round(secs / 60)} min ago`

interface TopStripProps {
  scheduleId: string
  iteration: string
  weeks: number[]
  selectedWeek: number
  onSelectWeek: (w: number) => void
  /** Per-week live results from useRunSchedulerDirect (running / complete / failed). */
  weekResults?: Record<number, WeekResult>
  /** The same from the survey ("Preparing data"), shown on the tiles while it runs. */
  prepareWeekResults?: Record<number, WeekResult>
  onConfigure: () => void
  onRun: () => void
  isRunning?: boolean
  progress?: SchedulerProgress | null
  /**
   * "Run scheduler" always runs the survey phase first (see
   * Scheduler/CLAUDE.md's Prepare/Run split notes) — these describe that
   * automatic first phase, not a separate user action. `isRunning` covers
   * the refine phase that follows it; the two never overlap.
   */
  isPreparing?: boolean
  prepareProgress?: SchedulerProgress | null
  /**
   * Seconds since the run (or the survey before it) last showed signs of
   * life, from the job-status poll. Null when unknown (an older API).
   */
  lastUpdateSecs?: number | null
  prepareLastUpdateSecs?: number | null
  /** Which reported answer the factory schedule is showing. */
  scheduleVariant: ScheduleVariantKey
  onSelectScheduleVariant: (key: ScheduleVariantKey) => void
  /** Set while a mid-week reschedule is on screen: its production end. The
   *  picker then shows the reschedule alone — the answer list describes the
   *  last fresh run, not the schedule being shown. */
  rescheduleMakespan?: number
}

/** Compact running-state label shown inside the Run button, covering both
 *  the automatic survey phase and the refine phase that follows it. */
const runLabel = (
  isRunning: boolean,
  isPreparing: boolean,
  progress?: SchedulerProgress | null,
  prepareProgress?: SchedulerProgress | null
) => {
  if (isPreparing) {
    const phase = prepareProgress?.phase
    if (phase === 'queued' || phase === 'starting') return 'Starting…'
    if (prepareProgress?.generation && prepareProgress?.total_generations) {
      return `Preparing ${prepareProgress.generation}/${prepareProgress.total_generations}`
    }
    return 'Preparing…'
  }
  if (!isRunning) return 'Run scheduler'
  if (progress?.generation && progress?.total_generations) {
    return `Gen ${progress.generation}/${progress.total_generations}`
  }
  return 'Running…'
}

/**
 * Command-bridge top strip: 8-week tile grid, Configure run, and Run scheduler.
 * Tiles show per-week schedule status and makespan after a run.
 */
export const SchedulerTopStrip = ({
  scheduleId,
  iteration,
  weeks,
  selectedWeek,
  onSelectWeek,
  weekResults,
  prepareWeekResults,
  onConfigure,
  onRun,
  isRunning = false,
  progress,
  isPreparing = false,
  prepareProgress,
  lastUpdateSecs = null,
  prepareLastUpdateSecs = null,
  scheduleVariant,
  onSelectScheduleVariant,
  rescheduleMakespan,
}: TopStripProps) => {
  // Decides what is OFFERED only — every run is refused server-side by
  // security.auth.requires('run') regardless of what renders here.
  const { canRun, canPublish } = usePermissions()
  const [publishOpen, setPublishOpen] = useState(false)
  const navigate = useNavigate()

  // Un-scoped (no week) fetch just for the schedule's versionDetails map —
  // same query shape ScheduleDataVersionsPanel/ScheduleViewPage already use
  // for the same purpose.
  const { data: baseData } = useSchedulerData(scheduleId, iteration)
  const versions = useMemo(() => {
    const raw = baseData?.schedule?.versionDetails
    if (!raw) return [] as { key: string; name: string }[]
    try {
      const parsed = JSON.parse(raw)
      return Object.keys(parsed)
        .map((key) => ({ key, name: parsed[key]?.name ?? `Version ${key}` }))
        .sort((a, b) => (parseInt(b.key, 10) || 0) - (parseInt(a.key, 10) || 0))
    } catch {
      return []
    }
  }, [baseData])
  const currentVersionLabel =
    versions.find((v) => v.key === iteration)?.name ?? `Version ${iteration}`

  const versionButtonRef = useRef<HTMLDivElement>(null)
  const [versionMenuOpen, setVersionMenuOpen] = useState(false)

  // The run's reported answers, for the Schedule picker beside Versions.
  // The manifest is written by a RUN, so an iteration last scheduled before
  // this feature has none at all — hence the Balanced fallback rather than an
  // empty or absent control. A control that disappears reads as the feature
  // being broken; one that says "Balanced" reads as "this run reported one
  // answer", which is the truth.
  const { data: scheduleVariants } = useScheduleVariants(
    scheduleId,
    iteration,
    selectedWeek
  )
  // Disruption hours are shown whenever the manifest carries them, for every
  // answer rather than just the one named after them. Every answer past the
  // fastest is deliberately allowed to finish LATER than the one before it,
  // so a row quoting makespan and cleans alone makes each of them look
  // strictly worse than the row above — their entire advantage sits in a
  // column the planner cannot see. Showing the figure on every row is what
  // makes the trade legible: "+24h to finish, 30h less disruption" is a
  // decision, "+24h" on its own is a mistake. It matters most for the
  // unnamed "Alternative" rows, whose label says nothing at all about what
  // they buy.
  //
  // Deliberately NOT a computed delta against the row above. The figures come
  // from the manifest and are exact; a delta would have to pick a baseline,
  // and every baseline is wrong for some row (the ladder is not a chain
  // through 'balanced', and 'balanced' is pinned first regardless of where it
  // finishes).
  //
  // Guarded rather than assumed: an iteration last run by an older API has a
  // manifest with no such column, and `0h disruption` would be a lie about a
  // schedule that simply never reported it (shown as a dash).
  //
  // All three rules are applied in ScheduleVariantSelect, which formats the
  // figures itself; this component only hands it the manifest rows.
  const [versionModalOpen, setVersionModalOpen] = useState(false)

  const onCreateVersionSuccess = () => {
    setVersionModalOpen(false)
    toast.success('New version created successfully')
    const newVersion = (
      Math.max(...versions.map((v) => parseInt(v.key, 10) || 0), 0) + 1
    ).toString()
    navigate(`/plan-scheduler/input/${scheduleId}/${newVersion}`)
  }
  const onCreateVersionError = () => {
    toast.error('Error creating new version')
  }
  const { mutate: createVersion, isLoading: isCreatingVersion } =
    useCreateScheduleVersion(onCreateVersionSuccess, onCreateVersionError)

  const handleSaveNewVersion = (formData: Record<string, unknown>) => {
    const time = formData['time'] as string
    createVersion({
      scheduleId,
      iteration,
      data: {
        ...formData,
        hoursSinceSundayMidnight: hoursSinceMondayMidnight(time),
      },
    })
  }

  // One query per week — React Query deduplicates and caches across components.
  const w1 = useSchedulerData(scheduleId, iteration, 1)
  const w2 = useSchedulerData(scheduleId, iteration, 2)
  const w3 = useSchedulerData(scheduleId, iteration, 3)
  const w4 = useSchedulerData(scheduleId, iteration, 4)
  const w5 = useSchedulerData(scheduleId, iteration, 5)
  const w6 = useSchedulerData(scheduleId, iteration, 6)
  const w7 = useSchedulerData(scheduleId, iteration, 7)
  const w8 = useSchedulerData(scheduleId, iteration, 8)

  // A multi-week run's per-week rows carry ABSOLUTE consolidated-horizon
  // times, so each tile has to report its own slice rather than where the
  // row ends in the whole run — otherwise week 2 of a 2-week solve reads the
  // run's 185h instead of the 17h it occupies. `undefined` here means the
  // rows are already per-week 0–168 and need no rebasing.
  const consolidatedWeeks = useConsolidatedWeeks(scheduleId, iteration)

  const weekDataMap = useMemo(() => {
    const queries = [w1, w2, w3, w4, w5, w6, w7, w8]
    return queries.reduce<
      Record<number, { hasData: boolean; products: number; makespan: number }>
    >((acc, q, i) => {
      const week = i + 1
      const rows = (q.data?.data ?? []) as InputRow[]
      // Through the shared helpers so this tile can never drift from the KPI
      // tile again — see makespan.ts.
      const makespan = makespanForWeek(
        rows,
        weekBaseHourFor(consolidatedWeeks, week)
      )
      acc[week] = {
        hasData: rows.length > 0,
        products: new Set(rows.map((r) => r.productCode).filter(Boolean)).size,
        makespan,
      }
      return acc
    }, {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    w1.data,
    w2.data,
    w3.data,
    w4.data,
    w5.data,
    w6.data,
    w7.data,
    w8.data,
    consolidatedWeeks,
  ])

  // The survey first: `isRunning` is also true while it runs (the schedule
  // is RUNNING from the moment Run is pressed), but the run hook has nothing
  // to show until the survey hands over. Checking it first hid the banner
  // for the whole survey, leaving only "Surveying…" on the button.
  const activeProgress = isPreparing
    ? prepareProgress
    : isRunning
      ? progress
      : null
  const pct =
    activeProgress?.generation !== undefined &&
    activeProgress?.total_generations
      ? Math.min(
          100,
          (activeProgress.generation / activeProgress.total_generations) * 100
        )
      : null
  const activeLastUpdate = isPreparing
    ? prepareLastUpdateSecs
    : isRunning
      ? lastUpdateSecs
      : null
  const stalled =
    activeLastUpdate !== null && activeLastUpdate >= STALL_WARNING_SECS
  const color = stalled ? TOKENS.stateFailed : stageColor(activeProgress?.stage)
  const activePhase = activeProgress?.phase
  // Reattached mid-survey: the run hook follows the survey into its run, so
  // until the hand-over its progress IS the survey's.
  const runHookOnSurvey =
    !isPreparing && isRunning && progress?.phase === 'preparing'
  const surveyProgress = isPreparing ? prepareProgress : progress
  const currentStep = stepIndex(isPreparing, activePhase)

  return (
    <>
      <Box
        sx={{
          background: TOKENS.surface0,
          borderBottom: `1px solid ${TOKENS.line300}`,
          flexShrink: 0,
          fontFamily: FONT_SANS,
        }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
          }}
        >
          {/* ── Week tiles ── */}
          {weeks.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: '5px' }}>
                {weeks.map((w) => {
                  // While the survey runs, a week it has reached shows the
                  // survey; otherwise the run's own result, as before.
                  // A page that reattached during the survey watches it
                  // through the RUN hook, whose weeks are then the survey's.
                  const pw = isPreparing
                    ? prepareWeekResults?.[w]
                    : runHookOnSurvey
                      ? weekResults?.[w]
                      : undefined
                  const isWkSurveying = pw?.status === 'running'
                  const isWkSurveyed = pw?.status === 'complete'
                  const wr =
                    isWkSurveying || isWkSurveyed ? undefined : weekResults?.[w]
                  const data = weekDataMap[w]

                  // Derive tile state. A surveyed week still has to be
                  // optimised, so it stays in the running colour.
                  const isWkRunning =
                    wr?.status === 'running' || isWkSurveying || isWkSurveyed
                  const isComplete =
                    !isWkRunning &&
                    (wr?.status === 'complete' || (!wr && data?.hasData))
                  const isActive = w === selectedWeek

                  // Design-spec colours
                  const dotColor = isComplete
                    ? TOKENS.stateComplete
                    : isWkRunning
                      ? TOKENS.stateRunning
                      : TOKENS.line300

                  const borderColor = isActive
                    ? TOKENS.brandTeal
                    : isComplete
                      ? TOKENS.stateComplete
                      : isWkRunning
                        ? TOKENS.stateRunning
                        : TOKENS.line200

                  const bgColor = isActive
                    ? TOKENS.tealTint
                    : isComplete
                      ? 'rgba(14,124,134,0.05)'
                      : isWkRunning
                        ? 'rgba(199,127,42,0.06)'
                        : '#fff'

                  // Value line
                  // The run's SSE number (`wr.makespan`) describes the
                  // run's OWN answer and nothing else, so it must not outlive
                  // the run: while it did, picking "Fewest cleans" left every
                  // week tile showing the default answer's makespan. The
                  // row-derived value follows the selection, because
                  // `useSchedulerData` reads ScheduleVariantContext. So the
                  // SSE value leads only while the job is streaming (rows for
                  // a just-completed week can still be the previous run's);
                  // once it is over, rows are the truth.
                  const makespanVal = isRunning
                    ? (wr?.makespan ?? data?.makespan)
                    : (data?.makespan ?? wr?.makespan)
                  const isThisWeekTracked =
                    isWkRunning && progress?.current_week === w
                  const isThisWeekSurveyed =
                    isWkSurveying && surveyProgress?.current_week === w
                  const valueStr = isWkSurveyed
                    ? 'Surveyed'
                    : isWkSurveying
                      ? isThisWeekSurveyed &&
                        surveyProgress?.generation &&
                        surveyProgress?.total_generations
                        ? `Survey ${surveyProgress.generation}/${surveyProgress.total_generations}`
                        : 'Surveying…'
                      : isThisWeekTracked
                        ? progress?.generation && progress?.total_generations
                          ? `Gen ${progress.generation}/${progress.total_generations}`
                          : 'Optimising…'
                        : isWkRunning
                          ? 'Optimising…'
                          : isComplete && makespanVal
                            ? `${makespanVal.toFixed(0)}h`
                            : '—'
                  const valueMono = isComplete && !!makespanVal

                  return (
                    <Box
                      key={w}
                      component="button"
                      onClick={() => onSelectWeek(w)}
                      sx={{
                        border: `${isActive ? 2 : 1}px solid ${borderColor}`,
                        background: bgColor,
                        borderRadius: '2px',
                        padding: isActive ? '5px 6px' : '6px 7px',
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        textAlign: 'left',
                        '&:hover': {
                          boxShadow: '0 1px 4px rgba(14,27,34,0.12)',
                        },
                      }}
                    >
                      {/* Dot + week label */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          mb: '3px',
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: dotColor,
                            flexShrink: 0,
                            ...(isWkRunning
                              ? { boxShadow: '0 0 0 3px rgba(199,127,42,0.18)' }
                              : {}),
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: TOKENS.ink700,
                            fontFamily: FONT_SANS,
                          }}
                        >
                          W{w}
                        </Box>
                      </Box>
                      {/* Value */}
                      <Box
                        sx={{
                          fontFamily: valueMono ? FONT_MONO : FONT_SANS,
                          fontSize: valueMono ? 11 : 9.5,
                          fontWeight: isWkRunning || isComplete ? 600 : 400,
                          color: isWkRunning
                            ? TOKENS.stateRunning
                            : isComplete
                              ? TOKENS.stateComplete
                              : TOKENS.ink300,
                        }}
                      >
                        {valueStr}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              <Box
                sx={{
                  width: '1px',
                  height: 30,
                  background: TOKENS.line200,
                  flexShrink: 0,
                }}
              />
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Which reported answer the factory schedule shows — the design-
            system picker (ScheduleVariantSelect): a ghost-pill trigger like
            the version control beside it, opening a small table of the
            answers, because they differ only in their numbers and a planner
            compares them column by column. Always rendered — an absent
            control is indistinguishable from a broken feature.

            The list is a LADDER, ordered by makespan (see VARIANT_ORDER in
            SchedulerView.data.ts): finish time rises down the list and
            disruption falls, so the cost of each option is the difference
            from the row above it. Up to seven rows — the four named answers
            plus up to three unnamed "Alternative" middle answers the engine
            found on the (makespan, disruption) Pareto staircase, which exist
            precisely because no label can express them. */}
          <ScheduleVariantSelect
            value={scheduleVariant}
            options={scheduleVariants}
            onChange={onSelectScheduleVariant}
            rescheduleMakespan={rescheduleMakespan}
          />

          {/* Version controls — immediately left of Configure run. */}
          <Box ref={versionButtonRef} sx={{ display: 'inline-flex' }}>
            <PillButton
              kind="ghost"
              size="sm"
              icon={<KeyboardArrowDownIcon />}
              onClick={() => setVersionMenuOpen(true)}
            >
              {currentVersionLabel}
            </PillButton>
          </Box>
          <Menu
            anchorEl={versionButtonRef.current}
            open={versionMenuOpen}
            onClose={() => setVersionMenuOpen(false)}
          >
            {versions.length === 0 && <MenuItem disabled>No versions</MenuItem>}
            {versions.map((v) => (
              <MenuItem
                key={v.key}
                selected={v.key === iteration}
                onClick={() => {
                  setVersionMenuOpen(false)
                  navigate(`/plan-scheduler/input/${scheduleId}/${v.key}`)
                }}
              >
                {v.name}
              </MenuItem>
            ))}
          </Menu>
          <PillButton
            kind="ghost"
            size="sm"
            icon={<AddIcon />}
            onClick={() => setVersionModalOpen(true)}
          >
            Create version
          </PillButton>

          <Box sx={{ width: '1px', height: 30, background: TOKENS.line200 }} />
          <PillButton
            kind="ghost"
            size="sm"
            icon={<TuneIcon />}
            onClick={onConfigure}
            disabled={isRunning || isPreparing || !canRun}
            title={
              canRun
                ? undefined
                : 'These settings only affect a run, which your role does not allow.'
            }
          >
            Configure run
          </PillButton>
          {/* Super User and above (deck slide 3) — but `canPublish` is now
              true whenever /me has not answered, so this hides only on an
              explicit denial. An unreachable service leaves the button where
              it was and the 403 does the talking. */}
          {canPublish && (
            <PillButton
              kind="ghost"
              size="sm"
              icon={<PublishIcon />}
              onClick={() => setPublishOpen(true)}
              disabled={isRunning || isPreparing}
              title={
                isRunning || isPreparing
                  ? 'Wait for the run to finish before publishing'
                  : 'Publish this plan for everyone to work from'
              }
            >
              Publish
            </PillButton>
          )}
          <PillButton
            kind="primary"
            size="sm"
            icon={
              isRunning || isPreparing ? (
                <HourglassTopIcon />
              ) : (
                <PlayArrowIcon />
              )
            }
            onClick={onRun}
            disabled={isRunning || isPreparing || !canRun}
            title={
              canRun ? undefined : 'Your role does not allow running schedules.'
            }
            sx={{ px: '14px' }}
          >
            {runLabel(isRunning, isPreparing, progress, prepareProgress)}
          </PillButton>
        </Box>

        {/* Prominent run/prepare-progress banner — visible above every tab,
          not just the control panel, since the default tab (Factory
          schedule) has nowhere else to surface generation progress. */}
        {(isRunning || isPreparing) && activeProgress && (
          <Box
            sx={{
              px: 1.75,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0,
                '@keyframes topStripPulse': {
                  '0%': { boxShadow: `0 0 0 0 ${color}66` },
                  '70%': { boxShadow: `0 0 0 7px ${color}00` },
                  '100%': { boxShadow: `0 0 0 0 ${color}00` },
                },
                animation: 'topStripPulse 1.6s ease-out infinite',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Where in the run we are: the four steps a planner thinks
                  in, with the current one named by the API's phase. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  mb: '4px',
                  fontSize: 10.5,
                }}
              >
                {RUN_STEPS.map((label, i) => {
                  const done = i < currentStep
                  const current = i === currentStep
                  return (
                    <Box
                      key={label}
                      sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {i > 0 && (
                        <Box
                          sx={{
                            width: 14,
                            height: '1px',
                            background: done
                              ? TOKENS.stateComplete
                              : TOKENS.line300,
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: current ? 700 : 500,
                          color: current
                            ? color
                            : done
                              ? TOKENS.stateComplete
                              : TOKENS.ink300,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: current
                              ? color
                              : done
                                ? TOKENS.stateComplete
                                : TOKENS.line300,
                          }}
                        />
                        {current && activePhase
                          ? (PHASE_LABEL[activePhase] ?? label)
                          : label}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  mb: '3px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: TOKENS.ink700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {activeProgress.message}
                  </Box>
                  {pct !== null && (
                    <Box
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color,
                        flexShrink: 0,
                      }}
                    >
                      Generation {activeProgress.generation}/
                      {activeProgress.total_generations}
                      <Box
                        component="span"
                        sx={{ color: TOKENS.ink400, ml: '6px' }}
                      >
                        ({pct.toFixed(0)}%)
                      </Box>
                    </Box>
                  )}
                </Box>
                {stalled ? (
                  // The API decides whether the worker is gone (and then
                  // fails the run with the reason); this only says that
                  // nothing has arrived, so the planner is not left
                  // watching a bar that stopped.
                  <Box
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: TOKENS.stateFailed,
                      flexShrink: 0,
                      ml: '10px',
                    }}
                  >
                    No update for{' '}
                    {activeLastUpdate! < 120
                      ? `${activeLastUpdate}s`
                      : `${Math.round(activeLastUpdate! / 60)} min`}
                    , checking the worker…
                  </Box>
                ) : activeLastUpdate !== null ? (
                  <Box
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: 10.5,
                      color: TOKENS.ink400,
                      flexShrink: 0,
                      ml: '10px',
                    }}
                  >
                    {sinceLabel(activeLastUpdate)}
                  </Box>
                ) : (
                  activeProgress.elapsed_ms !== undefined && (
                    <Box
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 10.5,
                        color: TOKENS.ink400,
                        flexShrink: 0,
                        ml: '10px',
                      }}
                    >
                      {(activeProgress.elapsed_ms / 1000).toFixed(1)}s elapsed
                    </Box>
                  )
                )}
              </Box>
              <LinearProgress
                variant={pct !== null ? 'determinate' : 'indeterminate'}
                value={pct ?? undefined}
                sx={{
                  height: 7,
                  borderRadius: 4,
                  bgcolor: TOKENS.line200,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: color,
                  },
                }}
              />
              {/* Planners read the bar as "wait here" (client feedback
                  2026-10-06). It runs on the server either way. */}
              <Box sx={{ mt: '3px', fontSize: 10.5, color: TOKENS.ink400 }}>
                You can leave this page: the run carries on, and the schedule
                list shows when it finishes.
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <CreateVersionDialog
        open={versionModalOpen}
        onClose={() => setVersionModalOpen(false)}
        isCreating={isCreatingVersion}
        onSave={handleSaveNewVersion}
      />
      <PublishScheduleDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        schedulerId={scheduleId}
        iteration={iteration}
      />
    </>
  )
}
